import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Link as LinkIcon, Upload, File as FileIcon, Loader2 } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { uploadFile as uploadFileService } from '@/services/uploadService';
import { useAppSelector } from '@/app/hooks';
import { selectIsAuthenticated } from '@/features/auth/authSlice';
import { Login } from '../auth/Login';

interface Creator20ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    artworkFiles: string[];
    artworkLinks: string[];
    socialLinks: string[];
    communicationMethod: string;
    comment: string;
  }) => void;
}

const COMMUNICATION_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'messenger', label: 'Messenger' },
];

export const Creator20Modal = ({ isOpen, onClose, onSubmit }: Creator20ModalProps) => {
  const { t } = useTranslation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [artworkFiles, setArtworkFiles] = useState<{ id: string; name: string }[]>([]);
  const [artworkLinks, setArtworkLinks] = useState<string[]>(['']);
  const [socialLinks, setSocialLinks] = useState<string[]>(['']);
  const [communicationMethod, setCommunicationMethod] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    setIsUploading(true);
    try {
      const newFiles: Array<{ id: string; name: string }> = [];
      for (const file of acceptedFiles) {
        const response = await uploadFileService(file);
        if (response.file?.id) {
          newFiles.push({
            id: response.file.id,
            name: file.name
          });
        }
      }
      setArtworkFiles(prev => [...prev, ...newFiles]);
    } catch (error) {
      console.error('File upload failed:', error);
      alert(t('uploader.error', 'Upload failed. Please try again.'));
    } finally {
      setIsUploading(false);
    }
  }, [t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.png', '.jpg', '.webp'] },
    multiple: true
  });

  if (!isOpen) return null;

  const handleRemoveFile = (id: string) => {
    setArtworkFiles(prev => prev.filter(f => f.id !== id));
  };

  const handleAddArtworkLink = () => setArtworkLinks([...artworkLinks, '']);
  const handleRemoveArtworkLink = (index: number) => {
    setArtworkLinks(artworkLinks.filter((_, i) => i !== index));
  };
  const handleArtworkLinkChange = (index: number, value: string) => {
    const newLinks = [...artworkLinks];
    newLinks[index] = value;
    setArtworkLinks(newLinks);
  };

  const handleAddSocialLink = () => setSocialLinks([...socialLinks, '']);
  const handleRemoveSocialLink = (index: number) => {
    setSocialLinks(socialLinks.filter((_, i) => i !== index));
  };
  const handleSocialLinkChange = (index: number, value: string) => {
    const newLinks = [...socialLinks];
    newLinks[index] = value;
    setSocialLinks(newLinks);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const validArtworkLinks = artworkLinks.filter(link => link.trim() !== '');

    if (validArtworkLinks.length === 0 && artworkFiles.length === 0) {
      alert(t('discountPrograms.errors.artworkRequired', 'At least one artwork example (file or link) is required'));
      setIsSubmitting(false);
      return;
    }

    try {
      await onSubmit({
        artworkFiles: artworkFiles.map(f => f.id),
        artworkLinks: validArtworkLinks,
        socialLinks: socialLinks.filter(link => link.trim() !== ''),
        communicationMethod,
        comment,
      });
      onClose();
    } catch (error) {
      console.error('Failed to submit application:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg bg-background p-6 shadow-lg">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1 hover:bg-muted"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-xl font-semibold">
          {t('discountPrograms.creator20.modalTitle', 'Apply for Creator Starter (20%)')}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('discountPrograms.creator20.modalDescription', 'Submit your portfolio and social links to qualify for 20% off all orders.')}
        </p>

        {!isAuthenticated ? (
          <div className="mt-6">
            <Login 
              standalone 
              initialMode="login" 
              onSuccess={() => {}} 
            />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* Artwork Examples */}
          <div className="space-y-3">
            <label className="text-sm font-medium">
              {t('discountPrograms.form.artworkExamples', 'Artwork Examples')}
              <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-muted-foreground">
              {t('discountPrograms.form.artworkDescription', 'Upload files or add links to your artwork (at least one required)')}
            </p>

            {/* File Upload Area */}
            <div
              {...getRootProps()}
              className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
                ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/20 hover:border-primary/50'}
                ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center gap-2">
                {isUploading ? (
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                ) : (
                  <Upload className="h-8 w-8 text-muted-foreground" />
                )}
                <div className="text-sm">
                  <span className="font-semibold text-primary">
                    {t('uploader.click_to_upload', 'Click to upload')}
                  </span>
                  {' '}{t('uploader.or_drag_drop', 'or drag and drop')}
                </div>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG or WEBP (max. 10MB)
                </p>
              </div>
            </div>

            {/* Uploaded Files List */}
            {artworkFiles.length > 0 && (
              <div className="space-y-2">
                {artworkFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-2 rounded-md border bg-muted/30">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="text-xs truncate">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(file.id)}
                      className="text-xs text-destructive hover:underline ml-2 shrink-0"
                    >
                      {t('common.remove', 'Remove')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="relative flex items-center gap-2">
              <div className="h-px flex-1 bg-muted-foreground/20" />
              <span className="text-[10px] uppercase font-bold text-muted-foreground/50 tracking-widest">
                {t('common.or', 'OR')}
              </span>
              <div className="h-px flex-1 bg-muted-foreground/20" />
            </div>

            {artworkLinks.map((link, index) => (
              <div key={index} className="flex gap-2">
                <div className="relative flex-1">
                  <LinkIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => handleArtworkLinkChange(index, e.target.value)}
                    placeholder={t('discountPrograms.form.artworkLinkPlaceholder', 'https://...')}
                    className="w-full rounded-md border bg-background pl-9 pr-3 py-2 text-sm"
                  />
                </div>
                {artworkLinks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveArtworkLink(index)}
                    className="rounded-md border px-3 py-2 text-sm hover:bg-muted"
                  >
                    {t('common.remove', 'Remove')}
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddArtworkLink}
              className="text-sm text-primary hover:underline"
            >
              + {t('discountPrograms.form.addAnotherLink', 'Add another link')}
            </button>
          </div>

          {/* Social Links */}
          <div className="space-y-3">
            <label className="text-sm font-medium">
              {t('discountPrograms.form.socialLinks', 'Social Media Links')}
            </label>
            <p className="text-xs text-muted-foreground">
              {t('discountPrograms.form.socialDescription', 'Share your Instagram, Twitter, Behance, etc.')}
            </p>

            {socialLinks.map((link, index) => (
              <div key={index} className="flex gap-2">
                <div className="relative flex-1">
                  <LinkIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => handleSocialLinkChange(index, e.target.value)}
                    placeholder={t('discountPrograms.form.socialLinkPlaceholder', 'https://instagram.com/...')}
                    className="w-full rounded-md border bg-background pl-9 pr-3 py-2 text-sm"
                  />
                </div>
                {socialLinks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveSocialLink(index)}
                    className="rounded-md border px-3 py-2 text-sm hover:bg-muted"
                  >
                    {t('common.remove', 'Remove')}
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddSocialLink}
              className="text-sm text-primary hover:underline"
            >
              + {t('discountPrograms.form.addAnotherLink', 'Add another link')}
            </button>
          </div>

          {/* Communication Method */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('discountPrograms.form.communicationMethod', 'Preferred Communication Method')}
            </label>
            <select
              value={communicationMethod}
              onChange={(e) => setCommunicationMethod(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              required
            >
              <option value="">{t('common.select', 'Select...')}</option>
              {COMMUNICATION_METHODS.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('discountPrograms.form.comment', 'Additional Comments')}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              placeholder={t('discountPrograms.form.commentPlaceholder', 'Tell us about your work...')}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background hover:bg-foreground/90 disabled:opacity-50"
            >
              {isSubmitting
                ? t('common.submitting', 'Submitting...')
                : t('common.submitApplication', 'Submit Application')}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};
